import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Loader2, RefreshCw } from "lucide-react";

const MONTH_LABELS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function buildMonthOptions() {
  // 12 meses pasados, mes actual, 12 meses futuros
  const out = [];
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 12);
  for (let i = 0; i < 25; i++) {
    const y = d.getFullYear();
    const m = d.getMonth();
    out.push({ value: `${y}-${String(m + 1).padStart(2, "0")}`, label: `${MONTH_LABELS[m]} ${y}` });
    d.setMonth(m + 1);
  }
  return out;
}

const STATUS_OPTS = [
  { value: "pendiente", label: "Pendiente", className: "bg-gray-100 text-gray-700" },
  { value: "en_proceso", label: "En Proceso", className: "bg-amber-50 text-amber-800" },
  { value: "finalizado", label: "Finalizado", className: "bg-emerald-50 text-emerald-800" },
  { value: "trip_terminado", label: "Trip Terminado", className: "bg-blue-50 text-blue-800" },
  { value: "cancelado", label: "Cancelado", className: "bg-red-50 text-red-700" },
];

function StatusBadge({ value, onChange }) {
  const opt = STATUS_OPTS.find((s) => s.value === value) || STATUS_OPTS[0];
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-8 border-0 text-xs font-medium px-2 ${opt.className}`} data-testid={`recon-status-${value}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTS.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

function TripStatusPill({ value }) {
  const tones = {
    Abierto: "bg-emerald-50 text-emerald-700",
    Cerrado: "bg-blue-50 text-blue-700",
    Cotizaciones: "bg-purple-50 text-purple-700",
    "Viaje de Empresa": "bg-indigo-50 text-indigo-700",
    Terminado: "bg-gray-100 text-gray-700",
    Archivado: "bg-zinc-100 text-zinc-600",
    Cancelado: "bg-red-50 text-red-700",
    "Post-Viaje": "bg-amber-50 text-amber-800",
  };
  const cls = tones[value] || "bg-gray-100 text-gray-700";
  return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${cls}`}>{value}</span>;
}

// Parsea "N(2)-C(0)-R(7)-F(0)-T(9)" y devuelve {N,C,R,F,T}
function parseServicesStatus(raw) {
  if (!raw) return null;
  const out = {};
  raw.replace(/[·•]/g, "-").split(/\s*-\s*/).forEach((tok) => {
    const m = tok.trim().match(/^([NCRFT])\s*\(\s*(\d+)\s*\)/i);
    if (m) out[m[1].toUpperCase()] = parseInt(m[2], 10);
  });
  return Object.keys(out).length ? out : null;
}

function ServiceStatusPills({ raw }) {
  const s = parseServicesStatus(raw);
  if (!s) return <span className="text-gray-300 text-xs">—</span>;
  // F = Reconfirmado (verde), R = Reservado (azul), C = Contactado (ámbar), N = No tocado (gris/rojo)
  const items = [
    { k: "T", label: "Total", cls: "bg-gray-100 text-gray-700" },
    { k: "F", label: "Reconfirmado", cls: "bg-emerald-100 text-emerald-800" },
    { k: "R", label: "Reservado", cls: "bg-blue-100 text-blue-800" },
    { k: "C", label: "Contactado", cls: "bg-amber-100 text-amber-800" },
    { k: "N", label: "No tocado", cls: s.N > 0 ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-500" },
  ];
  return (
    <div className="flex flex-wrap gap-1" title={`T=Total · F=Reconfirmado · R=Reservado · C=Contactado · N=No tocado`}>
      {items.map((it) => (
        <span key={it.k} className={`inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${it.cls}`}>
          <span className="opacity-70">{it.k}</span>
          <span className="font-semibold">{s[it.k] ?? 0}</span>
        </span>
      ))}
    </div>
  );
}

function SupportSelect({ value, options, onSave, testid }) {
  // Internal value "__none__" represents "Sin asignar" (UI placeholder for empty)
  const v = value || "__none__";
  return (
    <Select value={v} onValueChange={(nv) => onSave(nv === "__none__" ? "" : nv)}>
      <SelectTrigger className="h-8 text-xs" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__"><span className="text-gray-400">Sin asignar</span></SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function InlineTextCell({ value, onSave, placeholder, testid }) {
  // value comes from server; component is keyed by parent on server value,
  // so initial state is always the current server value and re-renders without resync.
  const [val, setVal] = useState(value || "");
  return (
    <Input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if ((val || "") !== (value || "")) onSave(val); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className="h-8 text-xs"
      placeholder={placeholder || "—"}
      data-testid={testid}
    />
  );
}

export default function ReconfirmationsPage() {
  const monthOpts = useMemo(() => buildMonthOptions(), []);
  const [month, setMonth] = useState(currentMonth());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [supportOptions, setSupportOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/reconfirmations/support-options")
      .then(({ data }) => { if (!cancelled) setSupportOptions(data.options || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/reconfirmations", { params: { month } });
        if (!cancelled) setItems(data);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Error al cargar reconfirmaciones");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [month]);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reconfirmations", { params: { month } });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar reconfirmaciones");
    } finally {
      setLoading(false);
    }
  };

  const onImport = async () => {
    if (!confirm(`¿Importar trips de ${monthLabel(month)} desde gestion.viajadverdad.com? Los registros existentes se actualizarán con los datos del scraping.`)) return;
    setImporting(true);
    try {
      const { data } = await api.post("/reconfirmations/import", { month });
      toast.success(`Importados ${data.total} trips (${data.created} nuevos, ${data.updated} actualizados)`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const onPatch = async (row, patch) => {
    // optimistic update
    setItems((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
    try {
      await api.patch(`/reconfirmations/${row.id}`, patch);
      if ("agente_support" in patch) {
        toast.success(`Traveler Support actualizado en gestion → "${patch.agente_support || "Sin asignar"}"`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
      reload();
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      (r.lead_name || "").toLowerCase().includes(q) ||
      (r.agente_ventas || "").toLowerCase().includes(q) ||
      (r.agente_support || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  // Totals (similares a los del Excel)
  const totals = useMemo(() => {
    const t = { total: items.length, finalizado: 0, pendiente: 0, en_proceso: 0, trip_terminado: 0, cancelado: 0, amenities: 0, mensaje_travefy: 0, no_budget: 0 };
    items.forEach((r) => {
      if (t[r.estado_reconfirmacion] !== undefined) t[r.estado_reconfirmacion]++;
      if (r.amenities) t.amenities++;
      if (r.mensaje_travefy) t.mensaje_travefy++;
      if (r.no_budget) t.no_budget++;
    });
    return t;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="reconfirmations-page">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <p className="vv-label">Operaciones</p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Reconfirmaciones</h1>
          <p className="text-sm text-gray-500 mt-1">Trips por mes de Arrival Date. Importados desde gestion.viajadverdad.com.</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="w-44">
            <label className="vv-label block mb-1">Mes</label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="recon-month-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {monthOpts.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-60">
            <label className="vv-label block mb-1">Buscar</label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cliente, agente..." data-testid="recon-search-input"/>
          </div>
          <Button variant="outline" onClick={reload} disabled={loading} data-testid="recon-refresh-button">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Refrescar
          </Button>
          <Button onClick={onImport} disabled={importing} className="bg-blue-600 hover:bg-blue-700" data-testid="recon-import-button">
            {importing ? <Loader2 size={14} className="mr-1 animate-spin"/> : <Download size={14} className="mr-1"/>}
            Importar desde gestion
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="recon-totals">
        <Stat label="Total Trips" value={totals.total} />
        <Stat label="Pendiente" value={totals.pendiente} tone="gray" />
        <Stat label="En Proceso" value={totals.en_proceso} tone="amber" />
        <Stat label="Finalizado" value={totals.finalizado} tone="emerald" />
        <Stat label="Trip Cerrado" value={totals.trip_terminado} tone="blue" />
        <Stat label="Amenities" value={totals.amenities} tone="indigo" />
        <Stat label="NO Budget" value={totals.no_budget} tone="red" />
      </div>

      <div className="vv-card overflow-x-auto">
        <table className="vv-table w-full text-sm">
          <thead>
            <tr>
              <th className="min-w-[180px]">Trip / Cliente</th>
              <th>Arrival</th>
              <th>Departure</th>
              <th className="text-center">Días</th>
              <th className="min-w-[260px]">Resumen Reservas</th>
              <th>Agente Ventas</th>
              <th className="min-w-[140px]">Agente Support</th>
              <th className="min-w-[150px]">Estado</th>
              <th className="text-center">Travefy</th>
              <th className="text-center">Amenities</th>
              <th className="text-center">NO Budget</th>
              <th className="min-w-[200px]">Notas Urgentes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={12} className="text-center py-12 text-gray-400">
                  No hay reconfirmaciones para este mes. Pulsa &quot;Importar desde gestion&quot; para traerlas.
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`recon-row-${r.external_trip_id}`}>
                <td>
                  <div className="font-medium">{r.lead_name}</div>
                  <div className="text-[11px] text-gray-400 mono">#{r.external_trip_id}</div>
                </td>
                <td className="mono text-xs">{formatDate(r.arrival_date)}</td>
                <td className="mono text-xs">{formatDate(r.departure_date)}</td>
                <td className="text-center text-xs">{r.duracion_dias ?? "—"}</td>
                <td>
                  <ServiceStatusPills raw={r.servicios_raw} />
                </td>
                <td className="text-xs">{r.agente_ventas || "—"}</td>
                <td>
                  <SupportSelect
                    value={r.agente_support}
                    options={supportOptions}
                    onSave={(v) => onPatch(r, { agente_support: v })}
                    testid={`recon-support-${r.external_trip_id}`}
                  />
                </td>
                <td>
                  <StatusBadge value={r.estado_reconfirmacion} onChange={(v) => onPatch(r, { estado_reconfirmacion: v })} />
                </td>
                <td className="text-center">
                  <Checkbox checked={r.mensaje_travefy} onCheckedChange={(v) => onPatch(r, { mensaje_travefy: !!v })} data-testid={`recon-travefy-${r.external_trip_id}`} />
                </td>
                <td className="text-center">
                  <Checkbox checked={r.amenities} onCheckedChange={(v) => onPatch(r, { amenities: !!v })} data-testid={`recon-amenities-${r.external_trip_id}`} />
                </td>
                <td className="text-center">
                  <Checkbox checked={r.no_budget} onCheckedChange={(v) => onPatch(r, { no_budget: !!v })} data-testid={`recon-nobudget-${r.external_trip_id}`} />
                </td>
                <td>
                  <InlineTextCell
                    key={`not-${r.id}-${r.notas_urgentes || ""}`}
                    value={r.notas_urgentes}
                    onSave={(v) => onPatch(r, { notas_urgentes: v })}
                    testid={`recon-notes-${r.external_trip_id}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function monthLabel(m) {
  if (!m) return "";
  const [y, mm] = m.split("-");
  return `${MONTH_LABELS[parseInt(mm, 10) - 1]} ${y}`;
}

function Stat({ label, value, tone = "gray" }) {
  const tones = {
    gray: "bg-gray-50 text-gray-700",
    amber: "bg-amber-50 text-amber-800",
    emerald: "bg-emerald-50 text-emerald-800",
    blue: "bg-blue-50 text-blue-800",
    indigo: "bg-indigo-50 text-indigo-800",
    red: "bg-red-50 text-red-800",
  };
  return (
    <div className={`vv-card p-3 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
